import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Heart, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { brand, featuredProducts } from "../data/estoreContent";
import { estoreProductUrl } from "../data/estorePaths";

function EstoreFeatured() {
  const navigate = useNavigate();
  const [wish, setWish] = useState(() => new Set([2, 5]));

  const toggle = (id) => {
    setWish((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box sx={{ py: { xs: 5, md: 8 }, bgcolor: "#fff", borderTop: `1px solid ${brand.stroke}`, borderBottom: `1px solid ${brand.stroke}` }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <Typography variant="overline" sx={{ color: brand.muted, letterSpacing: "0.2em", fontWeight: 700 }}>
            Featured
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: brand.text, mb: 1, letterSpacing: "-0.02em" }}>
            Handpicked for you
          </Typography>
          <Typography variant="body1" sx={{ color: brand.muted, mb: 4, maxWidth: 520 }}>
            Rich photography, calm layout, and motion that supports scanning — not distraction.
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {featuredProducts.map((p, i) => {
            const sale = p.discount > 0 ? Math.round(p.price * (1 - p.discount / 100)) : p.price;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  whileHover={{ y: -8 }}
                  style={{ height: "100%" }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      overflow: "hidden",
                      border: `1px solid ${brand.stroke}`,
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(estoreProductUrl(p.id))}
                  >
                    <Box sx={{ position: "relative", pt: "100%", bgcolor: "#F1F5F9" }}>
                      <Box
                        component="img"
                        src={p.image}
                        alt={p.title}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
                          "&:hover": { transform: "scale(1.05)" },
                        }}
                      />
                      {p.discount > 0 && (
                        <Chip
                          label={`${p.discount}% off`}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            fontWeight: 800,
                            bgcolor: "rgba(15,23,42,0.88)",
                            color: "#fff",
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(p.id);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(255,255,255,0.92)",
                          "&:hover": { bgcolor: "#fff" },
                        }}
                        aria-label="wishlist"
                      >
                        <motion.span animate={{ scale: wish.has(p.id) ? [1, 1.2, 1] : 1 }} transition={{ duration: 0.35 }}>
                          <Heart size={18} fill={wish.has(p.id) ? "#E11D48" : "none"} color={wish.has(p.id) ? "#E11D48" : brand.text} />
                        </motion.span>
                      </IconButton>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: brand.text, mb: 0.5 }}>
                        {p.title}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                        <Star size={16} fill="#FBBF24" color="#FBBF24" />
                        <Typography variant="caption" color="text.secondary">
                          {p.rating.toFixed(1)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2, mt: "auto" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          ₹{sale}
                        </Typography>
                        {p.discount > 0 && (
                          <Typography variant="body2" sx={{ textDecoration: "line-through", color: brand.muted }}>
                            ₹{p.price}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(estoreProductUrl(p.id));
                        }}
                        sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", borderColor: brand.stroke, color: brand.text }}
                      >
                        View details
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}

export default EstoreFeatured;
